import React, { useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useTable } from "react-table";
import { motion } from "framer-motion";

import { renderMonth, renderDollars } from "../../helpers/renderCells";
import { renderDate } from "../../helpers/Date";
import "../../styles/components/modals/_support-changes.scss";

import { setChangesModalData } from "../../actions/supporters";

import Modal from "../Modals/Modal";
import fadeInUp from "../../helpers/Animations";

const getDateKey = (date) => Math.floor(new Date(date).getTime() / 1000);

const SupportChangesModal = ({ isOpen, data, fullname, onClose }) => {
  const columns = useMemo(() => {
    const headers = [
      {
        accessor: "label",
      },
    ];

    const uniqueColumns = data
      .reverse()
      .filter(
        (value, index, self) =>
          index ===
          self.findIndex(
            (t, i) => renderDate(t.date) === renderDate(value.date)
          )
      )
      .reverse();

    // eslint-disable-next-line react/prop-types
    uniqueColumns.forEach(({ date }) => {
      headers.push({
        Header: renderDate(date),
        accessor: `${getDateKey(date)}`,
        columns: [
          {
            Header: "Before",
            accessor: `before_${getDateKey(date)}`,
          },
          {
            Header: "After",
            accessor: `after_${getDateKey(date)}`,
          },
        ],
      });
    });

    return headers;
  }, [data]);

  const rowData = useMemo(() => {
    const amount = {
      label: "Amount",
    };

    const frequency = {
      label: "Frequency",
    };

    const method = {
      label: "Method",
    };

    const status = {
      label: "Status",
    };

    // eslint-disable-next-line react/prop-types
    data.forEach(({ date, ...record }) => {
      amount[`before_${getDateKey(date)}`] = renderDollars(
        record.amount_before,
        "left"
      );
      amount[`after_${getDateKey(date)}`] =
        record.amount_before !== record.amount_after ? (
          <strong>{renderDollars(record.amount_after, "left")}</strong>
        ) : (
          renderDollars(record.amount_after, "left")
        );
      frequency[`before_${getDateKey(date)}`] = record.frequency_before;
      frequency[`after_${getDateKey(date)}`] =
        record.frequency_before !== record.frequency_after ? (
          <strong>{record.frequency_after}</strong>
        ) : (
          record.frequency_after
        );
      method[`before_${getDateKey(date)}`] = record.method_before;
      method[`after_${getDateKey(date)}`] =
        record.method_before !== record.method_after ? (
          <strong>{record.method_after}</strong>
        ) : (
          record.method_after
        );
      status[`before_${getDateKey(date)}`] = record.status_before;
      status[`after_${getDateKey(date)}`] =
        record.status_after !== record.status_before ? (
          <strong>{record.status_after}</strong>
        ) : (
          record.status_after
        );
    });

    return [status, frequency, method, amount];
  }, [data]);

  const { headerGroups, rows, prepareRow } = useTable({
    columns,
    data: rowData,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} modalName="support-changes-modal">
      <motion.div
        animate={fadeInUp.animate}
        transition={fadeInUp.transition}
        className="support-changes-modal__wrapper"
      >
        <h3>
          <strong>{fullname}</strong>
        </h3>
        <div className="table sticky">
          <div className="table__body" role="rowgroup">
            {headerGroups.map((headerGroup) => {
              const headerGroupProps = headerGroup.getHeaderGroupProps();
              return (
                <div
                  className="table__header"
                  key={headerGroupProps.key}
                  role={headerGroupProps.role}
                >
                  {headerGroup.headers.map((column) => {
                    const cellProps = column.getHeaderProps();
                    return (
                      <div
                        className="table__heading"
                        key={cellProps.key}
                        data-sticky-last-left-td={
                          cellProps["data-sticky-last-left-td"] !== undefined
                            ? cellProps["data-sticky-last-left-td"]
                            : null
                        }
                        data-sticky-td={
                          cellProps["data-sticky-td"] !== undefined
                            ? cellProps["data-sticky-td"]
                            : null
                        }
                        style={cellProps.style}
                      >
                        {column.render("Header")}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {rows.map((row) => {
              prepareRow(row);
              const rowProps = row.getRowProps();
              return (
                <div className="table__row" key={rowProps.key}>
                  {row.cells.map((cell) => {
                    const cellProps = cell.getCellProps();
                    return (
                      <div
                        key={cellProps.key}
                        className={`table__item table__item--${cell.column.id}`}
                        style={cellProps.style}
                        data-sticky-last-left-td={
                          cellProps["data-sticky-last-left-td"] !== undefined
                            ? cellProps["data-sticky-last-left-td"]
                            : null
                        }
                        data-sticky-td={
                          cellProps["data-sticky-td"] !== undefined
                            ? cellProps["data-sticky-td"]
                            : null
                        }
                        role="cell"
                      >
                        {cell.render("Cell")}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </Modal>
  );
};

SupportChangesModal.propTypes = {
  isOpen: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.object),
  fullname: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

SupportChangesModal.defaultProps = {
  isOpen: false,
  data: [],
  fullname: "",
};

const mapDispatchToProps = (dispatch) => ({
  onClose: () => dispatch(setChangesModalData([], "", false)),
});

const mapStateToProps = (state) => ({
  isOpen: state.supporters.changesModal.isOpen,
  data: state.supporters.changesModal.data,
  fullname: state.supporters.changesModal.fullname,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SupportChangesModal);
